import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play, ShieldCheck, Sparkles, Siren } from "lucide-react";
import { SITE } from "@/lib/site";
import { Container, CTAButton, Kicker } from "@/components/ui-kit";
import { BrowserFrame, Pill, Avatar, Bar } from "@/components/mockups/primitives";
import { JobsTable } from "@/components/mockups/core";

const fade = (d) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] } });

const Hero = () => (
  <section className="relative overflow-hidden" data-testid="hero">
    <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-70" aria-hidden />
    <div className="orb h-[520px] w-[520px] -top-40 -left-32 bg-mk-blue/30" aria-hidden />
    <div className="orb h-[460px] w-[460px] top-20 right-[-120px] bg-mk-lime/20" aria-hidden />

    <Container className="relative pt-16 pb-20 lg:pt-24 lg:pb-28">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-6 min-w-0">
          <motion.div {...fade(0)}>
            <Kicker>Multi-tenant · White-label · Production-grade</Kicker>
          </motion.div>
          <motion.h1 {...fade(0.08)} className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.98] text-mk-text">
            Fix more,<br />
            <span className="relative inline-block">
              <span className="text-mk-lime text-glow-lime">Manage less.</span>
            </span>
          </motion.h1>
          <motion.p {...fade(0.16)} className="mt-6 text-base md:text-lg text-mk-dim leading-relaxed max-w-xl">
            <span className="text-mk-text font-medium">Maintenance, properly engineered.</span> Fixlr is the operations platform for teams that run on minutes, not days — jobs, quotes, in-house operatives, supplier networks, invoices and live geo-tracked service delivery, all in one multi-tenant engine.
          </motion.p>
          <motion.div {...fade(0.24)} className="mt-8 flex flex-wrap items-center gap-3">
            <CTAButton href={SITE.signupUrl} testId="hero-start-trial-btn">Start free trial <ArrowRight className="h-4 w-4" /></CTAButton>
            <CTAButton to="/contact" variant="ghost" testId="hero-book-demo-btn"><Play className="h-4 w-4 text-mk-lime" /> Book a 20-min demo</CTAButton>
          </motion.div>
          <motion.div {...fade(0.32)} className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-mk-muted">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-mk-lime" />7-day free trial</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-mk-lime" />No credit card</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-mk-lime" />Sandbox pre-loaded with demo data</span>
          </motion.div>
        </div>

        <div className="lg:col-span-6 min-w-0 relative">
          <motion.div initial={{ opacity: 0, y: 40, rotateX: 8 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} style={{ perspective: 1200 }}>
            <BrowserFrame className="beam" bodyClass="scanline">
              <JobsTable compact />
            </BrowserFrame>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.7 }} className="hidden sm:block absolute sm:-right-6 -bottom-8 w-[260px] rounded-2xl glass p-3.5 glow-lime float-slow" data-testid="hero-ai-card">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-mk-lime"><Sparkles className="h-3 w-3" />AI supplier match</div>
            <div className="mt-2 flex items-center gap-2"><Avatar initials="NH" tone="lime" /><div className="flex-1 min-w-0"><div className="text-xs font-medium truncate">North Heat Ltd</div><div className="text-[10px] text-mk-muted">3.2 mi · 4.9★ · compliant</div></div><span className="font-display font-bold text-mk-lime">96</span></div>
            <Bar pct={96} className="mt-2" />
            <div className="mt-2 text-[10px] text-mk-muted">Ranked 14 suppliers in 0.4s</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.9 }} className="hidden sm:flex absolute sm:-left-10 bottom-14 rounded-2xl glass px-3.5 py-2.5 items-center gap-2.5" data-testid="hero-emergency-card">
            <span className="grid place-items-center h-8 w-8 rounded-full bg-red-500/20 text-red-300"><Siren className="h-4 w-4" /></span>
            <div><div className="text-xs font-medium">EMG-4E9D · Tier 1 acknowledged</div><div className="font-mono text-[10px] text-mk-muted">00:04 · SLA 03:41 remaining</div></div>
            <Pill tone="red">LIVE</Pill>
          </motion.div>
        </div>
      </div>
    </Container>
  </section>
);

export default Hero;
